/**
 * Layout shell for the InDeep PWA.
 * Provides navigation header with auth state awareness.
 *
 * Mobile navigation: hamburger menu with drawer pattern.
 * Desktop navigation: horizontal nav bar.
 * Authenticated users: top-right user menu with Profile and Account options.
 */
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { RoutePaths } from '@/routes';
import { useAuthStore, RegistrationKind } from '@/store/authStore';
import { BottomNav } from '@/components/BottomNav';

/**
 * Badge data for account role indicator.
 */
export interface RoleBadge {
  label: string;
  icon: string;
  ariaLabel: string;
  className: string;
}

/**
 * Returns role badge data for a given registration kind.
 * Returns null for unknown/malformed registration kinds (safe fallback - no guessing).
 *
 * @param registrationKind - The account's registration kind
 * @returns RoleBadge data or null if badge should not be shown
 */
export function accountRoleBadgeForRegistrationKind(
  registrationKind: RegistrationKind
): RoleBadge | null {
  if (registrationKind === 'sponsor') {
    return {
      label: 'Sponsor',
      icon: '✦',
      ariaLabel: 'Account type: Sponsor',
      className: 'role-badge role-badge-sponsor',
    };
  }

  if (registrationKind === 'friend') {
    return {
      label: 'Friend',
      icon: '♡',
      ariaLabel: 'Account type: Friend',
      className: 'role-badge role-badge-friend',
    };
  }

  // Unknown/malformed registration kind - safe fallback, no badge
  return null;
}

/**
 * Nav labels for authenticated users, role-aware.
 */
interface NavLabels {
  discover: string;
  intro: string;
  chat: string;
  account: string;
  sponsorProfile?: string;
}

/**
 * Returns role-aware nav labels based on registration kind.
 *
 * @param registrationKind - The account's registration kind
 * @returns Nav labels appropriate for the account role
 */
export function navLabelsForRegistrationKind(
  registrationKind: RegistrationKind
): NavLabels {
  if (registrationKind === 'friend') {
    return {
      discover: 'Sponsors',
      intro: 'Requests',
      chat: 'Messages',
      account: 'Account',
    };
  }

  if (registrationKind === 'sponsor') {
    return {
      discover: 'Discover',
      intro: 'Intro',
      chat: 'Chat',
      account: 'Account',
      sponsorProfile: 'Profile',
    };
  }

  // Unclassified users need onboarding, show minimal safe nav
  if (registrationKind === 'unclassified') {
    return {
      discover: 'Explore',
      intro: 'Requests',
      chat: 'Messages',
      account: 'Account',
    };
  }

  // Unknown/malformed registration kind - use neutral labels (safe fallback)
  // Avoids presenting sponsor-mode copy when role is unknown
  return {
    discover: 'Explore',
    intro: 'Requests',
    chat: 'Messages',
    account: 'Account',
  };
}

/**
 * Mobile breakpoint in pixels.
 * Below this width, use hamburger menu pattern.
 */
const MOBILE_BREAKPOINT = 640;

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);

  // Mobile menu open state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // User menu dropdown state
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Track if we're on mobile viewport
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );

  // Use authStatus as source of truth for authenticated state
  // This ensures we don't show authenticated nav during bootstrap loading
  // or when server has invalidated the session
  const { authStatus, registrationKind, logout } = useAuthStore();

  const isActive = (path: string) => location.pathname === path;
  const isCanonicalMobileRoute = location.pathname === RoutePaths.CHAT
    || location.pathname.startsWith('/chat/')
    || location.pathname === RoutePaths.ACCOUNT
    || location.pathname === RoutePaths.FEED;

  /**
   * Routes that own their canonical mobile-shell composition at
   * EVERY viewport — not only at <640px.
   *
   * Distinct from `isCanonicalMobileRoute`:
   * - `isCanonicalMobileRoute` covers /chat, /chat/* and /account
   *   and gates the BottomNav render + the canonical-mobile class.
   *   Its CSS gate (`@media (max-width: 639px)`) suppresses the
   *   global desktop chrome only on mobile, by design.
   * - `isFixedCanonicalSurface` is true ONLY for routes whose
   *   Figma canonical surface has no desktop variant at all — the
   *   surface itself IS the design (mobile-shell at 390×844).
   *   The repair binds chrome suppression to a route-bound class
   *   that fires at every viewport (see
   *   ACT-INDEEP-PRODUCTION-MY-PROFILE-LAYOUT-REPAIR01).
   *
   * Per ACT §7 this MUST NOT change /chat. Adding a new route here
   * requires a new Figma desktop absence verification first.
   */
  const isFixedCanonicalSurface = location.pathname === RoutePaths.ACCOUNT
    || location.pathname === RoutePaths.FEED;

  // Only show authenticated UI when server has validated the session
  // authStatus 'authenticated' = server validated; 'unknown'/'loading' = not yet confirmed
  const isServerAuthenticated = authStatus === 'authenticated';

  // Get role badge if registration kind is known AND session is validated
  const roleBadge = isServerAuthenticated && registrationKind
    ? accountRoleBadgeForRegistrationKind(registrationKind)
    : null;

  // Get role-aware nav labels
  const navLabels = navLabelsForRegistrationKind(registrationKind);

  // Update isMobile on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
      // Close mobile menu when switching to desktop
      if (window.innerWidth >= MOBILE_BREAKPOINT) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuOpen && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        // Check if click is on the menu toggle button
        const toggleButton = document.getElementById('mobile-menu-toggle');
        if (toggleButton && !toggleButton.contains(event.target as Node)) {
          setMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleUserMenuClickOutside = (event: MouseEvent) => {
      if (userMenuOpen && userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        // Check if click is on the user menu trigger
        const triggerButton = document.getElementById('user-menu-trigger');
        if (triggerButton && !triggerButton.contains(event.target as Node)) {
          setUserMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleUserMenuClickOutside);
    return () => document.removeEventListener('mousedown', handleUserMenuClickOutside);
  }, [userMenuOpen]);

  // Handle keyboard navigation
  const handleMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
    if (event.key === 'Escape' && userMenuOpen) {
      setUserMenuOpen(false);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(prev => !prev);
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(prev => !prev);
  };

  const closeUserMenu = () => {
    setUserMenuOpen(false);
  };

  return (
    <div
      className={
        `app-shell`
        + (isCanonicalMobileRoute ? ' app-shell--canonical-mobile' : '')
        + (isFixedCanonicalSurface ? ' app-shell--fixed-canonical-surface' : '')
      }
    >
      <header className="app-header">
        <nav className="app-nav" aria-label="Main navigation">
          {/* Left group: Logo + Badge */}
          <div className="nav-left">
            <Link to={RoutePaths.HOME} className="nav-brand">
              InDeep
            </Link>

            {roleBadge && (
              <span
                className={`nav-badge ${roleBadge.className}`}
                aria-label={roleBadge.ariaLabel}
                role="img"
              >
                {roleBadge.icon} {roleBadge.label}
              </span>
            )}
          </div>

          {/* Desktop navigation links - hidden on mobile */}
          <div className="nav-links-desktop">
            {/* Show authenticated nav only when server has validated the session */}
            {isServerAuthenticated && (
              <>
                {registrationKind === 'sponsor' && navLabels.sponsorProfile && (
                  <Link
                    to={RoutePaths.SPONSOR_PROFILE}
                    className={isActive(RoutePaths.SPONSOR_PROFILE) ? 'active' : ''}
                    data-testid="nav-sponsor-profile"
                  >
                    {navLabels.sponsorProfile}
                  </Link>
                )}
                <Link
                  to={RoutePaths.DISCOVER}
                  className={isActive(RoutePaths.DISCOVER) ? 'active' : ''}
                >
                  {navLabels.discover}
                </Link>
                <Link
                  to={RoutePaths.INTRO_REQUEST}
                  className={isActive(RoutePaths.INTRO_REQUEST) ? 'active' : ''}
                >
                  {navLabels.intro}
                </Link>
                <Link
                  to={RoutePaths.CHAT}
                  className={isActive(RoutePaths.CHAT) ? 'active' : ''}
                >
                  {navLabels.chat}
                </Link>
                <Link
                  to={RoutePaths.ACCOUNT}
                  className={isActive(RoutePaths.ACCOUNT) ? 'active' : ''}
                >
                  {navLabels.account}
                </Link>
              </>
            )}

            {/* Show public nav when not server-authenticated */}
            {!isServerAuthenticated && (
              <>
                <Link
                  to={RoutePaths.HOME}
                  className={isActive(RoutePaths.HOME) ? 'active' : ''}
                >
                  Login
                </Link>
                <Link
                  to={RoutePaths.REGISTER}
                  className={isActive(RoutePaths.REGISTER) ? 'active' : ''}
                >
                  Register
                </Link>
              </>
            )}

            {/* Show logout only when server has validated the session */}
            {isServerAuthenticated && (
              <button onClick={logout} className="nav-logout" data-testid="nav-logout">
                Logout
              </button>
            )}
          </div>

          {/* Right group: User menu trigger for authenticated users */}
          {isServerAuthenticated && (
            <div className="nav-right" ref={userMenuRef}>
              <button
                id="user-menu-trigger"
                className="user-menu-trigger"
                onClick={toggleUserMenu}
                aria-expanded={userMenuOpen}
                aria-haspopup="menu"
                aria-label="Open user menu"
                data-testid="user-menu-trigger"
              >
                <span className="user-icon" aria-hidden="true">👤</span>
              </button>

              {/* User menu dropdown */}
              {userMenuOpen && (
                <div
                  className="user-menu-dropdown"
                  role="menu"
                  data-testid="user-menu-dropdown"
                  onKeyDown={handleMenuKeyDown}
                >
                  {/*
                    REQ-200: For unclassified users (early activation journey state),
                    the dropdown contains ONLY Profile entry. No Account, no Logout
                    in this early journey state per the customer journey requirement.

                    Profile link is role-aware:
                    - unclassified: go to profile completion (Profile-only menu per REQ-200)
                    - sponsor: go to sponsor profile
                    - friend/unknown: go to account
                  */}
                  {registrationKind === 'unclassified' ? (
                    // REQ-200: Profile-only menu for unclassified users in activation journey
                    <Link
                      to={RoutePaths.PROFILE_COMPLETION}
                      className="user-menu-item"
                      role="menuitem"
                      onClick={closeUserMenu}
                      data-testid="user-menu-profile"
                    >
                      Profile
                    </Link>
                  ) : (
                    // Full menu for sponsor/friend/unknown users
                    <>
                      <Link
                        to={
                          registrationKind === 'sponsor'
                            ? RoutePaths.SPONSOR_PROFILE
                            : RoutePaths.ACCOUNT
                        }
                        className="user-menu-item"
                        role="menuitem"
                        onClick={closeUserMenu}
                        data-testid="user-menu-profile"
                      >
                        Profile
                      </Link>
                      <Link
                        to={RoutePaths.ACCOUNT}
                        className="user-menu-item"
                        role="menuitem"
                        onClick={closeUserMenu}
                        data-testid="user-menu-account"
                      >
                        Account
                      </Link>
                      <button
                        onClick={() => {
                          closeUserMenu();
                          logout();
                        }}
                        className="user-menu-item user-menu-logout"
                        role="menuitem"
                        data-testid="user-menu-logout"
                      >
                        Logout
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Mobile menu toggle button - only rendered on mobile */}
          {isServerAuthenticated && isMobile && (
            <button
              id="mobile-menu-toggle"
              className="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? '✕' : '☰'}
            </button>
          )}
        </nav>

        {/* Mobile menu drawer - conditionally rendered only when open on mobile */}
        {isServerAuthenticated && isMobile && mobileMenuOpen && (
          <div
            id="mobile-menu"
            ref={menuRef}
            className="mobile-menu mobile-menu-open"
            onKeyDown={handleMenuKeyDown}
            role="menu"
            data-testid="mobile-menu"
          >
            {registrationKind === 'sponsor' && navLabels.sponsorProfile && (
              <Link
                to={RoutePaths.SPONSOR_PROFILE}
                className={`mobile-menu-item ${isActive(RoutePaths.SPONSOR_PROFILE) ? 'active' : ''}`}
                role="menuitem"
                data-testid="mobile-nav-sponsor-profile"
              >
                {navLabels.sponsorProfile}
              </Link>
            )}
            <Link
              to={RoutePaths.DISCOVER}
              className={`mobile-menu-item ${isActive(RoutePaths.DISCOVER) ? 'active' : ''}`}
              role="menuitem"
              data-testid="mobile-nav-discover"
            >
              {navLabels.discover}
            </Link>
            <Link
              to={RoutePaths.INTRO_REQUEST}
              className={`mobile-menu-item ${isActive(RoutePaths.INTRO_REQUEST) ? 'active' : ''}`}
              role="menuitem"
              data-testid="mobile-nav-intro"
            >
              {navLabels.intro}
            </Link>
            <Link
              to={RoutePaths.CHAT}
              className={`mobile-menu-item ${isActive(RoutePaths.CHAT) ? 'active' : ''}`}
              role="menuitem"
              data-testid="mobile-nav-chat"
            >
              {navLabels.chat}
            </Link>
            <Link
              to={RoutePaths.ACCOUNT}
              className={`mobile-menu-item ${isActive(RoutePaths.ACCOUNT) ? 'active' : ''}`}
              role="menuitem"
              data-testid="mobile-nav-account"
            >
              {navLabels.account}
            </Link>
            <button
              onClick={logout}
              className="mobile-menu-item mobile-menu-logout"
              role="menuitem"
              data-testid="mobile-nav-logout"
            >
              Logout
            </button>
          </div>
        )}
      </header>

      <main
        className={
          `app-main`
          + (isCanonicalMobileRoute ? ' app-main--canonical-mobile' : '')
          + (isFixedCanonicalSurface ? ' app-main--fixed-canonical-surface' : '')
        }
      >{children}</main>

      {isServerAuthenticated && isCanonicalMobileRoute && <BottomNav />}

      <footer className="app-footer">
        <Link to={RoutePaths.CONTACT}>Contact</Link>
        <Link to={RoutePaths.PRIVACY}>Privacy</Link>
        <Link to={RoutePaths.REPORT}>Report</Link>
      </footer>

      <style>{`
        .app-shell {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
        }
        .app-header {
          background: var(--color-surface);
          border-bottom: 1px solid var(--color-border);
          padding: 0 var(--spacing);
          position: relative;
        }
        .app-nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          max-width: 600px;
          margin: 0 auto;
          height: 56px;
        }
        .nav-left {
          display: flex;
          align-items: center;
          gap: var(--spacing);
        }
        .nav-brand {
          font-weight: 700;
          font-size: 1.2rem;
        }
        .nav-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: var(--radius);
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .nav-badge.role-badge-friend {
          background: var(--color-border);
          color: var(--color-text-muted);
        }
        .nav-badge.role-badge-sponsor {
          background: var(--color-accent);
          color: var(--color-surface);
        }

        /* Desktop navigation */
        .nav-links-desktop {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .nav-links-desktop a {
          color: var(--color-text-muted);
          font-size: 0.9rem;
          padding: 8px 4px;
        }
        .nav-links-desktop a.active {
          color: var(--color-text);
        }
        .nav-links-desktop a:hover {
          color: var(--color-text);
          text-decoration: none;
        }
        .nav-logout {
          background: var(--color-accent);
          border: 1px solid var(--color-accent);
          color: #3C3C3C;
          padding: 6px 12px;
          border-radius: var(--radius);
          font-size: 0.85rem;
          font-weight: 500;
          margin-left: 8px;
        }
        .nav-logout:hover {
          opacity: 0.85;
        }

        /* User menu trigger and dropdown */
        .nav-right {
          position: relative;
        }
        .user-menu-trigger {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 1px solid var(--color-border);
          background: var(--color-surface);
          cursor: pointer;
          padding: 0;
          transition: border-color 0.15s ease;
        }
        .user-menu-trigger:hover {
          border-color: var(--color-accent);
        }
        .user-menu-trigger:focus {
          outline: 2px solid var(--color-accent);
          outline-offset: 2px;
        }
        .user-icon {
          font-size: 1rem;
          line-height: 1;
        }
        .user-menu-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          right: 0;
          min-width: 140px;
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          z-index: 100;
          overflow: hidden;
        }
        .user-menu-item {
          display: block;
          width: 100%;
          padding: 12px 16px;
          color: var(--color-text-muted);
          font-size: 0.9rem;
          text-decoration: none;
          border: none;
          background: transparent;
          text-align: left;
          cursor: pointer;
          box-sizing: border-box;
        }
        .user-menu-item:hover,
        .user-menu-item:focus {
          background: var(--color-border);
          color: var(--color-text);
          text-decoration: none;
        }
        .user-menu-item:focus {
          outline: 2px solid var(--color-accent);
          outline-offset: -2px;
        }
        .user-menu-logout {
          border-top: 1px solid var(--color-border);
          color: var(--color-text);
        }

        /* Mobile menu toggle button */
        .mobile-menu-toggle {
          display: none;
          background: transparent;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          padding: 8px 12px;
          font-size: 1rem;
          color: var(--color-text);
          cursor: pointer;
        }
        .mobile-menu-toggle:focus {
          outline: 2px solid var(--color-accent);
          outline-offset: 2px;
        }

        /* Mobile menu drawer */
        .mobile-menu {
          display: none;
          flex-direction: column;
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: var(--color-surface);
          border-bottom: 1px solid var(--color-border);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          z-index: 50;
        }
        .mobile-menu.mobile-menu-open {
          display: flex;
        }
        .mobile-menu-item {
          display: block;
          padding: 16px var(--spacing);
          color: var(--color-text-muted);
          font-size: 1rem;
          text-decoration: none;
          border: none;
          background: transparent;
          text-align: left;
          cursor: pointer;
          width: 100%;
          min-height: 44px;
          box-sizing: border-box;
        }
        .mobile-menu-item:hover,
        .mobile-menu-item:focus {
          background: var(--color-border);
          color: var(--color-text);
          text-decoration: none;
        }
        .mobile-menu-item:focus {
          outline: 2px solid var(--color-accent);
          outline-offset: -2px;
        }
        .mobile-menu-item.active {
          color: var(--color-text);
          font-weight: 500;
        }
        .mobile-menu-logout {
          border-top: 1px solid var(--color-border);
          color: var(--color-text);
        }

        /* Desktop styles */
        @media (min-width: 640px) {
          .nav-links-desktop {
            display: flex;
          }
          .mobile-menu-toggle {
            display: none;
          }
          .mobile-menu {
            display: none !important;
          }
        }

        /*
         * ACT-INDEEP-PRODUCTION-MY-PROFILE-LAYOUT-REPAIR01
         *
         * Fixed canonical surface — the route owns its canonical
         * mobile-shell composition at EVERY viewport.
         *
         * Distinct from .app-shell--canonical-mobile (whose chrome-
         * hide rule lives inside @media (max-width: 639px) for /chat).
         * For fixed-canonical-surface routes the surface itself IS
         * the design at desktop too (Figma mobile-shell, 390x844,
         * BottomNav) so the global desktop chrome must be hidden
         * unconditionally. The existing @media-bound rules for
         * .app-shell--canonical-mobile are NOT modified.
         */
        .app-shell--fixed-canonical-surface .app-header,
        .app-shell--fixed-canonical-surface .app-footer {
          display: none;
        }
        .app-shell--fixed-canonical-surface .app-main,
        .app-shell--fixed-canonical-surface .app-main--canonical-mobile,
        .app-shell--fixed-canonical-surface .app-main--fixed-canonical-surface {
          display: flex;
          flex: 1;
          min-height: 0;
          padding: 0;
          /*
           * ACT-INDEEP-PRODUCTION-MY-PROFILE-LAYOUT-REPAIR01-CORRECTION01
           *
           * P0-01 FIXED_SURFACE_GEOMETRY_AUTHORITY — bind the canonical
           * mobile-shell composition to its Figma S018 horizontal width
           * at EVERY viewport. Without this, .app-main inherits the
           * flex column's cross-axis stretch and renders as wide as
           * the desktop viewport (e.g. 1280px) while the BottomNav is
           * independently re-enabled by BottomNav.css — yielding a
           * horizontally incoherent shell at desktop (Playwright B-5
           * width_delta ~= 877px observed).
           *
           * 390px is the Figma S018 canonical surface width
           * (corpus.v1.json:658 — node 39:4695). The test invariant is
           * profile.width &lt;= canonicalMaxWidth AND
           * abs(profile.width - bottomNav.width) &lt;= tolerance;
           * both elements MUST share the same bounded width.
           *
           * The .app-main element here is a flex column child; the
           * parent .app-shell has flex-direction: column with the
           * default align-items: stretch, so children cross-stretch
           * to the viewport. max-width caps the used width and
           * margin-inline: auto centers the surface within the
           * available viewport width (flex child auto-margins absorb
           * leftover cross-axis space — see MDN max-width and
           * https://drafts.csswg.org/css-flexbox/#auto-margins).
           */
          max-width: 390px;
          width: 100%;
          margin-inline: auto;
        }

        /* Mobile styles */
        @media (max-width: 639px) {
          .app-shell--canonical-mobile .app-header,
          .app-shell--canonical-mobile .app-footer {
            display: none;
          }
          .app-shell--canonical-mobile .app-main--canonical-mobile {
            display: flex;
            min-height: 0;
            padding: 0;
          }
          .nav-links-desktop {
            display: none;
          }
          .mobile-menu-toggle {
            display: block;
          }
          .app-nav {
            max-width: 100%;
          }
          .nav-right {
            display: none;
          }
        }

        .app-main {
          flex: 1;
          padding: var(--spacing);
        }
        .app-footer {
          display: flex;
          gap: var(--spacing);
          justify-content: center;
          padding: var(--spacing);
          border-top: 1px solid var(--color-border);
          font-size: 0.85rem;
        }
        .app-footer a {
          color: var(--color-text-muted);
        }

        .chat-channel-placeholder {
          display: flex;
          flex: 1;
          min-height: 0;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 12px;
          padding: 24px 20px;
          color: #596b78;
          text-align: center;
        }
        .chat-channel-placeholder h1 {
          color: #1b2632;
          font-size: 20px;
        }
        .chat-channel-placeholder a {
          min-height: 44px;
          padding: 12px;
          color: #2ba89a;
        }
        .chat-channel-placeholder a:focus-visible {
          outline: 3px solid #2ba89a;
          outline-offset: 2px;
        }

        /* Legacy role-badge classes for backwards compatibility */
        .role-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: var(--radius);
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .role-badge-friend {
          background: var(--color-border);
          color: var(--color-text-muted);
        }
        .role-badge-sponsor {
          background: var(--color-accent);
          color: var(--color-surface);
        }
      `}</style>
    </div>
  );
}
